import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { TourProvider } from '@/contexts/Tour/Provider';
import { ErrorBoundary } from '@/features/errors/ErrorBoundary';
import { ErrorBoundaryAuth } from '@/features/errors/ErrorBoundaryAuth';
import { ErrorBoundaryCollections } from '@/features/errors/ErrorBoundaryCollections';
import { ErrorBoundaryDashboard } from '@/features/errors/ErrorBoundaryDashboard';
import { ErrorBoundaryDatasets } from '@/features/errors/ErrorBoundaryDatasets';
import { ErrorBoundaryDocuments } from '@/features/errors/ErrorBoundaryDocuments';
import { ErrorBoundarySpaces } from '@/features/errors/ErrorBoundarySpaces';
import { ErrorBoundaryUser } from '@/features/errors/ErrorBoundaryUser';
import AddGroups from '@/features/user-management/pages/components/AddGroups';
import { protectLogin, protectRoute } from '@/middlewares/auth';

const AuthLayout = lazy(() => import('@/features/auth/layouts/AuthLayout'));
const Login = lazy(() => import('@/features/auth/pages/Login'));
const Register = lazy(() => import('@/features/auth/pages/Register'));
const ResetPassword = lazy(() => import('@/features/auth/pages/ResetPassword'));
const CreatePassword = lazy(() => import('@/features/auth/pages/CreatePassword'));

const DashboardLayout = lazy(() => import('@/features/dashboard/DashboardLayout'));
const SpacesLayout = lazy(() => import('@/features/spaces'));
const DatasetsLayout = lazy(() => import('@/features/datasets'));
const CollectionsLayout = lazy(() => import('@/features/collections/CollectionsLayout'));
const ProfileSettings = lazy(() => import('@/features/user/pages/profile-settings'));
const UserManagementLayout = lazy(() => import('@/features/user-management/pages/UserManagementLayout'));
const AddUsers = lazy(() => import('@/features/user-management/pages/components/AddUsers'));
const ViewGroup = lazy(() => import('@/features/user-management/pages/components/ViewGroup'));

const SharingProfile = lazy(() => import('@/features/sharing-profile/pages/SharingProfile'));
const CreateSharingProfileStepper = lazy(() => import('@/features/sharing-profile/components/CreateSharingProfileStepper'));
const ImportExternal = lazy(() => import('@/features/import-external'));
const Queries = lazy(() => import('@/features/queries/Queries'));

const Help = lazy(() => import('@/features/onboarding/help'));

const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-gold" />
  </div>
);

const AppRouter = () => (
  <Router>
    <ErrorBoundary>
      <Routes>
        <Route element={protectLogin(AuthLayout)} path="/">
          <Route index element={<Navigate replace to="/dashboard" />} />
          <Route
            element={
              <ErrorBoundaryAuth>
                <Suspense fallback={<LoadingScreen />}>
                  <Login />
                </Suspense>
              </ErrorBoundaryAuth>
            }
            path="login"
          />
          <Route
            element={
              <ErrorBoundaryAuth>
                <Suspense fallback={<LoadingScreen />}>
                  <Register />
                </Suspense>
              </ErrorBoundaryAuth>
            }
            path="register"
          />
          <Route
            element={
              <ErrorBoundaryAuth>
                <Suspense fallback={<LoadingScreen />}>
                  <ResetPassword />
                </Suspense>
              </ErrorBoundaryAuth>
            }
            path="reset-password"
          />
          <Route
            element={
              <ErrorBoundaryAuth>
                <Suspense fallback={<LoadingScreen />}>
                  <ResetPassword />
                </Suspense>
              </ErrorBoundaryAuth>
            }
            path="reset-password/:token"
          />
          <Route
            element={
              <ErrorBoundaryAuth>
                <Suspense fallback={<LoadingScreen />}>
                  <CreatePassword />
                </Suspense>
              </ErrorBoundaryAuth>
            }
            path="create-password"
          />
        </Route>

        <Route
          element={
            <ErrorBoundaryUser>
              <TourProvider>{protectRoute(DashboardLayout)}</TourProvider>
            </ErrorBoundaryUser>
          }
          path="/dashboard/*"
        >
          <Route
            index
            element={
              <ErrorBoundaryDashboard>
                <Navigate replace to="spaces" />
              </ErrorBoundaryDashboard>
            }
          />
          <Route
            element={
              <ErrorBoundarySpaces>
                <Suspense fallback={<LoadingScreen />}>
                  <SpacesLayout />
                </Suspense>
              </ErrorBoundarySpaces>
            }
            path="spaces"
          />
          <Route path="spaces/:spaceId/datasets">
            <Route
              index
              element={
                <ErrorBoundaryDatasets>
                  <Suspense fallback={<LoadingScreen />}>
                    <DatasetsLayout />
                  </Suspense>
                </ErrorBoundaryDatasets>
              }
            />
            <Route
              element={
                <ErrorBoundaryCollections>
                  <Suspense fallback={<LoadingScreen />}>
                    <CollectionsLayout />
                  </Suspense>
                </ErrorBoundaryCollections>
              }
              path=":datasetId"
            />
          </Route>
          <Route path="spaces/:spaceId/datasets/:datasetId/collections">
            <Route
              index
              element={
                <ErrorBoundaryCollections>
                  <Suspense fallback={<LoadingScreen />}>
                    <CollectionsLayout />
                  </Suspense>
                </ErrorBoundaryCollections>
              }
            />
            <Route
              element={
                <ErrorBoundaryDocuments>
                  <Suspense fallback={<LoadingScreen />}>
                    <CollectionsLayout />
                  </Suspense>
                </ErrorBoundaryDocuments>
              }
              path=":collectionId"
            />
          </Route>
          <Route
            element={
              <ErrorBoundaryAuth>
                <Suspense fallback={<LoadingScreen />}>
                  <ProfileSettings />
                </Suspense>
              </ErrorBoundaryAuth>
            }
            path="profile-settings"
          />
          <Route path="spaces/:spaceId/user-management">
            <Route index element={<UserManagementLayout />} />
            <Route element={<UserManagementLayout />} path=":tabName" />
            <Route element={<AddUsers />} path="users/add" />
            <Route element={<ViewGroup />} path="groups/:groupId" />
            <Route element={<AddGroups />} path="groups/create" />
            {/* <Route path="groups/create" element={<CreateGroup />} /> */}
          </Route>
          <Route path="spaces/:spaceId/sharing-profile">
            <Route index element={<SharingProfile />} />
            <Route element={<CreateSharingProfileStepper />} path="create" />
          </Route>
          <Route element={<ImportExternal />} path="spaces/:spaceId/import-external" />
          <Route element={<Queries />} path="spaces/:spaceId/queries" />
          <Route element={<Help />} path="help" />
        </Route>

        <Route element={protectRoute(() => <h1>404 Not found</h1>)} path="*" />
      </Routes>
    </ErrorBoundary>
  </Router>
);

export default AppRouter;
